import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  BulkAddTourEvent,
  bulkAddTourEvent,
} from '@/domain/project-tour-ontour/actions/bulk-add-tour-event.action';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import {
  defaultTourEventFormValues,
  TourEventsFormValues,
} from '../tour-event-form-values/TourEventsFormValues';
import { AddTourEventsTable } from './AddTourEventsTable';
import { FooterWithButtons } from './FooterWithButtons';

export function ModalAddTourEvents({
  open,
  onClose,
  initialTourEvents,
  sequenceId,
}: {
  open: boolean;
  onClose: () => void;
  initialTourEvents: TourEventsFormValues;
  sequenceId: number;
}) {
  const dispatch = useAppDispatch();

  const { handleSubmit, register, control, setValue, reset, formState } =
    useForm<TourEventsFormValues>({
      defaultValues: {
        tourEvents: [defaultTourEventFormValues],
      },
    });

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'tourEvents',
  });

  const { errors } = formState;

  useEffect(() => {
    if (initialTourEvents.tourEvents.length === 0) {
      reset();
    } else {
      // init values
      setValue('tourEvents', initialTourEvents.tourEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTourEvents]);

  const onClickAddTourEvent = () => {
    insert(fields.length, defaultTourEventFormValues);
  };

  const onClickDeleteTourEvents = (index: number) => {
    remove(index);
  };

  const onSubmit = async (data: TourEventsFormValues) => {
    if (data.tourEvents.length === 0) {
      return;
    }
    const tourEvents = data.tourEvents.map((tourEvent) => ({
      name: tourEvent.name,
      date: dayjs(tourEvent.date).toISOString(),
      position: tourEvent.position,
      capacity: parseInt(tourEvent.capacity.toString()),
      type: tourEvent.type,
    }));
    const params: BulkAddTourEvent = {
      tourEvents: tourEvents,
      sequenceId: sequenceId,
    };
    try {
      await dispatch(bulkAddTourEvent(params)).unwrap();
      resetAndClose();
    } catch (error) {
      console.log(error);
    }
  };

  const resetAndClose = () => {
    remove();
    reset();
    onClose();
  };

  return (
    <FSModal
      open={open}
      onClose={resetAndClose}
      maxWidth={1200}
      disableCloseOnBackdropClick={true}
    >
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle
            title={translate('ProjectsTour.OnTour.AddTourEventsModal.title')}
          />
          <CloseButton onClick={resetAndClose} />
        </Stack>
        <AddTourEventsTable
          fields={fields}
          control={control}
          register={register}
          errors={errors}
          onClickAddTourEvent={onClickAddTourEvent}
          onClickDeleteTourEvent={onClickDeleteTourEvents}
        />
        <FooterWithButtons
          onClickSubmit={handleSubmit(onSubmit)}
          onClickCancel={() => resetAndClose()}
        />
      </Stack>
    </FSModal>
  );
}
